import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Premium Global Loading Spinner Component
 * Used consistently across the application for all loading states
 * 
 * @param {Object} props
 * @param {String} props.size - Size variant: 'sm', 'md', 'lg', 'full' (default: 'md')
 * @param {String} props.message - Optional loading message
 * @param {Boolean} props.overlay - Show as full-screen overlay
 * @param {String} props.variant - Style variant: 'primary', 'white' (default: 'primary')
 */
const LoadingSpinner = ({
    size = 'md',
    message = 'Loading...',
    overlay = false,
    variant = 'primary'
}) => {
    // Size configurations
    const sizeConfig = {
        sm: {
            spinner: 24,
            text: 'text-xs'
        },
        md: {
            spinner: 40,
            text: 'text-sm'
        },
        lg: {
            spinner: 56,
            text: 'text-base'
        },
        full: {
            spinner: 72,
            text: 'text-lg'
        }
    };

    const config = sizeConfig[size] || sizeConfig.md;

    // Color variants
    const colorClass = variant === 'white'
        ? 'text-white'
        : 'text-blue-600';

    const textColorClass = variant === 'white'
        ? 'text-white'
        : 'text-gray-600';

    // Render spinner content
    const spinnerContent = (
        <div className="flex flex-col items-center justify-center gap-4">
            {/* Animated spinner with pulse effect */}
            <div className="relative">
                {/* Outer pulse ring */}
                <div className={`absolute inset-0 ${colorClass} opacity-20 rounded-full animate-ping`}></div>

                {/* Main spinner */}
                <Loader2
                    size={config.spinner}
                    className={`${colorClass} animate-spin relative z-10`}
                    strokeWidth={2.5}
                />
            </div>

            {/* Loading message */}
            {message && (
                <div className="flex flex-col items-center gap-1">
                    <p className={`${textColorClass} font-semibold ${config.text} tracking-wide`}>
                        {message}
                    </p>
                    {/* Animated dots */}
                    <div className="flex gap-1">
                        <div className={`w-1.5 h-1.5 ${colorClass} rounded-full animate-bounce`} style={{ animationDelay: '0ms' }}></div>
                        <div className={`w-1.5 h-1.5 ${colorClass} rounded-full animate-bounce`} style={{ animationDelay: '150ms' }}></div>
                        <div className={`w-1.5 h-1.5 ${colorClass} rounded-full animate-bounce`} style={{ animationDelay: '300ms' }}></div>
                    </div>
                </div>
            )}
        </div>
    );

    // Full screen overlay variant
    if (overlay) {
        return (
            <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                {spinnerContent}
            </div>
        );
    }

    // Inline variant
    return (
        <div className="flex items-center justify-center py-12">
            {spinnerContent}
        </div>
    );
};

export default LoadingSpinner;
